import React, { useState, useEffect } from "react";
import AxiosInstance from "./../../services/AxiosInstance";
import { TASKS_ACTIVITY_URL } from "../../Constants/Constants";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import Timeline from "@mui/lab/Timeline";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Skeleton from "@mui/material/Skeleton";
import Divider from "@mui/material/Divider";
import InfoIcon from "@mui/icons-material/Info";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn"; // status
import FlagIcon from "@mui/icons-material/Flag"; // priority
import PersonIcon from "@mui/icons-material/Person"; // assignee
// Use EditIcon (pencil) for title changes
import DescriptionIcon from "@mui/icons-material/Description"; // description
import EventIcon from "@mui/icons-material/Event"; // due
import EditIcon from "@mui/icons-material/Edit";

// Helper for avatar initials
function stringAvatar(name) {
  if (!name) return "";
  const parts = name.split(" ");
  return parts.length > 1
    ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    : `${parts[0][0]}`.toUpperCase();
  // End of getActivityIcons
}
// Helper to get time ago
function getTimeAgo(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  const now = new Date();
  const diffInMinutes = Math.floor((now - date) / (1000 * 60));
  if (diffInMinutes < 1) return "Just now";
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
  return date.toLocaleString();
}

// Helper to get all changed field icons for an activity
function getActivityIcons(activity) {
  const icons = [];
  if (activity.status !== activity.newStatus)
    icons.push(<AssignmentTurnedInIcon fontSize="small" key="status" />);
  if (activity.priority !== activity.newPriority)
    icons.push(<FlagIcon fontSize="small" key="priority" />);
  if (activity.assignee !== activity.newAssignee)
    icons.push(<PersonIcon fontSize="small" key="assignee" />);
  if (activity.title !== activity.newTitle)
    icons.push(<EditIcon fontSize="small" key="title" />);
  if (activity.description !== activity.newDescription)
    icons.push(<DescriptionIcon fontSize="small" key="description" />);
  if (activity.due !== activity.newDue)
    icons.push(<EventIcon fontSize="small" key="due" />);
  if (icons.length === 0) icons.push(<EditIcon fontSize="small" key="edit" />);
  return icons;
}

// Helper to get activity color
function getActivityColor(activity) {
  if (activity.status !== activity.newStatus) return "success";
  if (activity.priority !== activity.newPriority) return "warning";
  if (activity.assignee !== activity.newAssignee) return "primary";
  if (activity.title !== activity.newTitle) return "info";
  if (activity.description !== activity.newDescription) return "secondary";
  return "primary";
}

// Helper to format change description for task
function formatChangeDescription(activity) {
  const changes = [];
  if (activity.priority !== activity.newPriority) {
    if (activity.priority === null) {
      changes.push({
        type: "priority",
        text: `Priority set to ${activity.newPriority}`,
        newValue: activity.newPriority,
      });
    } else {
      changes.push({
        type: "priority",
        text: `Priority changed from ${activity.priority} to ${activity.newPriority}`,
        oldValue: activity.priority,
        newValue: activity.newPriority,
      });
    }
  }
  if (activity.due !== activity.newDue) {
    if (activity.due === null) {
      changes.push({
        type: "due",
        text: `Due date set to ${activity.newDue}`,
        newValue: activity.newDue,
      });
    } else {
      changes.push({
        type: "due",
        text: `Due date changed from ${activity.due} to ${activity.newDue}`,
        oldValue: activity.due,
        newValue: activity.newDue,
      });
    }
  }
  if (activity.description !== activity.newDescription) {
    if (activity.description === null) {
      changes.push({
        type: "description",
        text: "Description added",
        newValue: activity.newDescription,
      });
    } else {
      changes.push({
        type: "description",
        text: `Description changed from ${activity.description} to ${activity.newDescription}`,
        oldValue: activity.description,
        newValue: activity.newDescription,
      });
    }
  }
  if (activity.title !== activity.newTitle) {
    if (activity.title === null) {
      changes.push({
        type: "title",
        text: `Title set to ${activity.newTitle}`,
        newValue: activity.newTitle,
      });
    } else {
      changes.push({
        type: "title",
        text: `Title changed from ${activity.title} to ${activity.newTitle}`,
        oldValue: activity.title,
        newValue: activity.newTitle,
      });
    }
  }
  if (activity.status !== activity.newStatus) {
    if (activity.status === null) {
      changes.push({
        type: "status",
        text: `Status set to ${activity.newStatus}`,
        newValue: activity.newStatus,
      });
    } else {
      changes.push({
        type: "status",
        text: `Status changed from ${activity.status} to ${activity.newStatus}`,
        oldValue: activity.status,
        newValue: activity.newStatus,
      });
    }
  }
  if (activity.assignee !== activity.newAssignee) {
    if (activity.assignee === null) {
      changes.push({
        type: "assignee",
        text: `Assignee set to ${activity.newAssignee}`,
        newValue: activity.newAssignee,
      });
    } else {
      changes.push({
        type: "assignee",
        text: `Assignee changed from ${activity.assignee} to ${activity.newAssignee}`,
        oldValue: activity.assignee,
        newValue: activity.newAssignee,
      });
    }
  }
  return changes;
}

function TaskActivity({ taskId }) {
  const theme = useTheme();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    AxiosInstance.get(`${TASKS_ACTIVITY_URL}/${taskId}`)
      .then((response) => {
        setActivities(response.data);
      })
      .catch((err) => {
        setError("Failed to load activity");
      })
      .finally(() => setLoading(false));
  }, [taskId]);

  if (loading) {
    return (
      <Box
        sx={{
          width: "100%",
          bgcolor: theme.palette.background.default,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.background.paper,
          }}
        >
          <Skeleton variant="text" width="40%" height={24} />
        </Box>
        <Box sx={{ p: 2 }}>
          <Timeline position="left" sx={{ p: 0, m: 0 }}>
            {[...Array(3)].map((_, i) => (
              <TimelineItem key={i} sx={{ "&:before": { display: "none" } }}>
                <TimelineSeparator>
                  <Skeleton variant="circular" width={40} height={40} />
                  {i < 2 && (
                    <TimelineConnector
                      sx={{
                        bgcolor: theme.palette.divider,
                        width: 2,
                        height: 40,
                      }}
                    />
                  )}
                </TimelineSeparator>
                <TimelineContent sx={{ py: 1, px: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Skeleton variant="circular" width={32} height={32} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <Skeleton variant="text" width="30%" height={16} />
                        <Skeleton variant="text" width="20%" height={14} />
                      </Box>
                      <Skeleton variant="text" width="80%" height={16} />
                      <Skeleton variant="text" width="60%" height={16} />
                    </Box>
                  </Box>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!activities || activities.length === 0) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "calc(100vh - 150px)",
          bgcolor: theme.palette.background.default,
          p: 2,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <InfoIcon color="action" sx={{ fontSize: 48, mb: 2, opacity: 0.7 }} />
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          No activity recorded yet
        </Typography>
        <Typography variant="body2" color="text.disabled">
          All task updates and changes will appear here.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: theme.palette.background.default,
        p: 2,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
      }}
    >
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        <Timeline position="left" sx={{ p: 0, m: 0 }}>
          {activities.map((activity, index) => {
            const changes = formatChangeDescription(activity);
            const isLast = index === activities.length - 1;
            return (
              <TimelineItem
                key={activity.id}
                sx={{ "&:before": { display: "none" } }}
              >
                <TimelineSeparator>
                  <TimelineDot
                    color={getActivityColor(activity)}
                    sx={{
                      bgcolor: theme.palette[getActivityColor(activity)].main,
                      color:
                        theme.palette[getActivityColor(activity)].contrastText,
                      position: "relative",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0.2,
                      }}
                    >
                      {getActivityIcons(activity)
                        .slice(0, 2)
                        .map((icon, idx) => icon)}
                      {getActivityIcons(activity).length > 2 && (
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "0.6rem",
                            fontWeight: "bold",
                            color: "inherit",
                          }}
                        >
                          +{getActivityIcons(activity).length - 2}
                        </Typography>
                      )}
                    </Box>
                  </TimelineDot>
                  {!isLast && (
                    <TimelineConnector
                      sx={{ bgcolor: theme.palette.divider, width: 2 }}
                    />
                  )}
                </TimelineSeparator>
                <TimelineContent sx={{ py: 1, px: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      "&:hover": { bgcolor: theme.palette.action.hover },
                      transition: "all 0.2s ease",
                    }}
                  >
                    <Avatar
                      alt={activity.modifiedBy}
                      src={`/static/images/avatar/${activity.id}.jpg`}
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: theme.palette.primary.main,
                        fontWeight: 600,
                        fontSize: 14,
                        color: theme.palette.getContrastText(
                          theme.palette.primary.main
                        ),
                      }}
                    >
                      {stringAvatar(activity.modifiedBy)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography
                          fontWeight={600}
                          variant="body2"
                          sx={{
                            color: theme.palette.text.primary,
                            fontSize: "0.875rem",
                          }}
                        >
                          {activity.modifiedBy}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.text.secondary,
                            fontSize: "0.75rem",
                          }}
                        >
                          {getTimeAgo(activity.createdOn)}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                        }}
                      >
                        {changes.map((change, changeIndex) => (
                          <Box
                            key={changeIndex}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              flexWrap: "wrap",
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: theme.palette.text.primary,
                                fontSize: "0.875rem",
                                lineHeight: 1.4,
                              }}
                            >
                              {change.text}
                            </Typography>
                            {change.newValue && (
                              <Chip
                                label={change.newValue}
                                size="small"
                                color={getActivityColor(activity)}
                                variant="outlined"
                                sx={{
                                  fontSize: "0.7rem",
                                  height: 20,
                                  "& .MuiChip-label": { px: 1 },
                                }}
                              />
                            )}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </TimelineContent>
              </TimelineItem>
            );
          })}
        </Timeline>
      </Box>
    </Box>
  );
}

export default TaskActivity;
